// Imported first so page modules can override the shared primitives.
import ui from './ui.module.css';
import { findCircleEntryKey } from '@circles/convex';
import type { FunctionReturnType } from 'convex/server';
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Landing } from '../landing/Landing';
import { useAccountKey } from '../lib/account';
import { api, convexClient } from '../lib/convex';
import { isIos, isStandalone, promptInstall } from '../lib/install';
import { useCachedQueries, useCachedQuery } from '../lib/liveQuery';
import { MemberAvatar } from '../lib/MemberAvatar';
import { avatarTint } from '../lib/avatarTint';
import { prefetchDocument } from '../lib/prefetch';
import { pushPermission } from '../lib/push';
import { applyUpdate, dismissUpdate, useUpdateAvailable } from '../lib/updates';
import {
  addDeviceToken,
  findDeviceTokenKey,
  getDeviceToken,
  removeDeviceToken,
  renameDeviceToken,
  useDeviceTokens,
} from '../lib/tokens';
import { initial, isImageIcon, safeAppPath, tint } from './format';
import styles from './Home.module.css';
import { errorCode, useCanPromptInstall, useDelayed, useLauncherPage, useSafeQuery } from './hooks';
import { AddSquareIcon, BellIcon, EyeIcon, GearIcon, LogoMark, WrenchIcon } from './icons';
import { IosInstallSteps } from './InstallHelp';
import { requestPush, syncPushSubscription } from './pushSync';
import { NOTIFY_CARD_SNOOZE_MS, notifyCardState } from './notifyCard';
import { SettingsSheet } from './SettingsSheet';
import {
  dismiss,
  getLastCircle,
  getStashedToken,
  isDismissed,
  rememberMarkedInstalled,
  renameCircleKey,
  setLastCircle,
  snooze,
  snoozedUntil,
  stashToken,
  wasMarkedInstalled,
} from './storage';

const NOTIFY_CARD = 'notifyCard';

type HomeData = FunctionReturnType<typeof api.launcher.home>;

/** Device tokens that already got their heartbeat during this page load. */
const heartbeatSent = new Set<string>();

/** Moves a device-token entry (and its launcher memory) to the circle identifier it belongs under. */
function adoptCircleKey(from: string | null, to: string): void {
  if (!from || from === to) return;
  renameDeviceToken(from, to);
  renameCircleKey(from, to);
}

/** Route "/" — circles as tabs, apps as tiles, inbox button. */
export function Home() {
  useLauncherPage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tokens = useDeviceTokens();
  const [notes, setNotes] = useState<string[]>([]);
  const [resolvingPreview, setResolvingPreview] = useState(() => params.has('previewToken'));
  const [selected, setSelected] = useState<string | null>(getLastCircle);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const updateAvailable = useUpdateAvailable();

  const slugs = Object.keys(tokens);
  const last = getLastCircle();
  const current = selected && tokens[selected] ? selected : last && tokens[last] ? last : (slugs[0] ?? null);
  const returnPath = safeAppPath(params.get('return'));
  const returnId = returnPath?.split('/')[2] ?? null;
  const returnSlug = returnId ? findCircleEntryKey(slugs, returnId) : null;

  const argsBySlug = useMemo(
    () => Object.fromEntries(Object.entries(tokens).map(([slug, e]) => [slug, { deviceToken: e.token }])),
    [tokens],
  );
  // Cached results paint at once; anything that acts on a home (the ?return bounce, heartbeat,
  // push) waits for the live one, since a cached home says nothing about the token still working.
  const homes = useCachedQueries(api.launcher.home, argsBySlug);
  const liveHome = (slug: string) => (homes[slug]?.stale ? undefined : homes[slug]?.data);
  const returnHome = returnSlug ? homes[returnSlug] : undefined;
  const publicKey = useSafeQuery(api.push.publicKey, {});

  const addNote = (note: string) => setNotes((n) => (n.includes(note) ? n : [...n, note]));

  // Owner "view as member": /?previewToken=<token>[&circle=<slug>]
  useEffect(() => {
    const previewToken = params.get('previewToken');
    if (!previewToken) return;
    let cancelled = false;
    convexClient
      .query(api.launcher.home, { deviceToken: previewToken })
      .then((home) => {
        if (cancelled) return;
        const slug = home.circle.publicId;
        adoptCircleKey(findDeviceTokenKey(home.circle.id), slug);
        const existing = getDeviceToken(slug);
        if (existing && existing.token !== previewToken && !getStashedToken(slug)) stashToken(slug, existing);
        addDeviceToken(slug, {
          token: previewToken,
          circleId: home.circle.id,
          circleName: home.circle.name,
          memberId: home.me.id,
          memberName: home.me.name,
        });
        setLastCircle(slug);
        setSelected(slug);
      })
      .catch(() => {
        if (!cancelled) addNote('This preview link no longer works. Open it again from the builder.');
      })
      .finally(() => {
        if (cancelled) return;
        setResolvingPreview(false);
        navigate('/', { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The runtime sends tokenless (or revoked) app visits here with ?return=<app path>.
  // Only bounce back once the token is known to work; a revoked one is dropped below.
  const returnHandled = useRef(false);
  useEffect(() => {
    if (!returnPath || returnHandled.current) return;
    if (!returnSlug || !tokens[returnSlug]) {
      returnHandled.current = true;
      addNote("You'll need an invite link to open that app. Ask whoever runs that circle to send it.");
      navigate('/', { replace: true });
    } else if (returnSlug && liveHome(returnSlug)) {
      returnHandled.current = true;
      window.location.replace(returnPath);
    } else if (returnHome?.error) {
      returnHandled.current = true;
      navigate('/', { replace: true });
    }
  }, [returnPath, returnSlug !== null && liveHome(returnSlug) !== undefined, returnHome?.error != null]);

  // Entries an older launcher stored under the bare slug move to the circle identifier.
  const renames = slugs.flatMap((key) => {
    const id = homes[key]?.data?.circle.publicId;
    return id && id !== key ? [[key, id] as const] : [];
  });
  useEffect(() => {
    if (renames.length === 0) return;
    for (const [from, to] of renames) {
      adoptCircleKey(from, to);
      if (selected === from) setSelected(to);
    }
  }, [renames.map((r) => r.join('>')).join(',')]);

  // A revoked device (or a deleted circle) gets UNAUTHORIZED: drop the token.
  const unauthorized = slugs.filter((s) => errorCode(homes[s]?.error) === 'UNAUTHORIZED');
  useEffect(() => {
    if (unauthorized.length === 0) return;
    for (const slug of unauthorized) {
      const name = tokens[slug]?.circleName ?? slug;
      removeDeviceToken(slug);
      const stashed = getStashedToken(slug);
      if (stashed) {
        addDeviceToken(slug, stashed);
        stashToken(slug, null);
      }
      addNote(`This device was signed out of ${name}. Open the invite link again to rejoin.`);
    }
  }, [unauthorized.join(',')]);

  // Heartbeat, installed flag and push subscription, once each circle has loaded.
  const loadedKey = slugs.map((s) => `${s}:${liveHome(s)?.preview ?? '-'}`).join(',');
  useEffect(() => {
    const standalone = isStandalone();
    const pushTokens: string[] = [];
    for (const slug of slugs) {
      const data = liveHome(slug);
      const entry = tokens[slug];
      if (!data || !entry) continue;
      const deviceToken = entry.token;
      if (!heartbeatSent.has(deviceToken)) {
        heartbeatSent.add(deviceToken);
        convexClient.mutation(api.devices.heartbeat, { deviceToken, userAgent: navigator.userAgent }).catch(() => {});
      }
      if (data.preview) continue;
      pushTokens.push(deviceToken);
      if (standalone && !wasMarkedInstalled(deviceToken)) {
        convexClient
          .mutation(api.devices.markInstalled, { deviceToken })
          .then(() => rememberMarkedInstalled(deviceToken))
          .catch(() => {});
      }
    }
    if (publicKey.data && pushTokens.length > 0) {
      syncPushSubscription(publicKey.data, pushTokens).catch((err: unknown) => console.warn('Circles: push sync failed', err));
    }
  }, [loadedKey, publicKey.data]);

  function select(slug: string) {
    setSelected(slug);
    setLastCircle(slug);
  }

  function leave(slug: string) {
    const entry = tokens[slug];
    if (entry) convexClient.mutation(api.devices.leave, { deviceToken: entry.token }).catch(() => {});
    removeDeviceToken(slug);
    const stashed = getStashedToken(slug);
    if (stashed) {
      addDeviceToken(slug, stashed);
      stashToken(slug, null);
    }
  }

  const currentHome = current ? homes[current] : undefined;
  const currentData = currentHome?.data;
  // Every hook runs before the early return below, whatever the URL.
  const builder = useBuilderLink(currentData?.circle.id ?? null);

  if (returnPath && returnSlug && tokens[returnSlug] && !returnHome?.error) return null;

  const totalUnread = slugs.reduce((sum, s) => sum + (homes[s]?.data?.unreadCount ?? 0), 0);
  const live = !resolvingPreview && currentData !== undefined && !currentHome?.stale;
  const settingsButton = (
    <button className={styles.iconButton} onClick={() => setSettingsOpen(true)} aria-label="Settings">
      <GearIcon size={23} />
    </button>
  );
  const settings = (
    <SettingsSheet
      open={settingsOpen}
      onClose={() => setSettingsOpen(false)}
      onJoined={() => {
        setSettingsOpen(false);
        setSelected(getLastCircle());
      }}
      device={
        current && currentData && !currentData.preview
          ? {
              circleName: currentData.circle.name,
              memberName: currentData.me.name,
              memberId: currentData.me.id,
              avatarUrl: currentData.me.avatarUrl,
              avatarKind: currentData.me.avatarKind,
              avatarHue: currentData.me.avatarHue,
              deviceToken: tokens[current]!.token,
              onSwitch: () => navigate(`/join/${currentData.circle.inviteCode}?switch=1`),
              onLeave: () => {
                setSettingsOpen(false);
                leave(current);
              },
            }
          : null
      }
    />
  );

  // No circle on this device: the landing (public, or owner mode with a builder key). Kept inside
  // Home so a token dropped here (Leave, revoked, stopped preview) switches over with its note.
  if (slugs.length === 0 && !resolvingPreview) {
    return <Landing notice={<Notes notes={notes} onDismiss={(n) => setNotes((all) => all.filter((x) => x !== n))} />} />;
  }

  return (
    <>
      {currentData?.preview && current && (
        <div className={styles.previewBar}>
          <EyeIcon size={18} />
          <span className={styles.previewText}>
            <span>
              Previewing as <strong>{currentData.me.name}</strong>
            </span>
            <span className={styles.previewNote} data-testid="preview-note">
              Read receipts are not recorded
            </span>
          </span>
          <button onClick={() => leave(current)}>Stop</button>
        </div>
      )}
      <main className={ui.page} data-ready={live ? 'true' : undefined}>
        <header className={styles.topBar}>
          <LogoMark size={34} />
          <div className={styles.topActions}>
            {currentHome?.stale && <Updating />}
            {builder && (
              <Link to={builder.href} className={styles.builderLink} data-testid="home-builder-link">
                <WrenchIcon size={17} />
                Builder
              </Link>
            )}
            <Link
              to="/inbox"
              className={styles.iconButton}
              aria-label={`Inbox${totalUnread ? `, ${totalUnread} unread` : ''}`}
            >
              <BellIcon size={24} />
              {totalUnread > 0 && <span className={styles.badge}>{totalUnread > 99 ? '99+' : totalUnread}</span>}
            </Link>
            {settingsButton}
          </div>
        </header>

        {updateAvailable && <UpdatePill />}

        {slugs.length > 1 && (
          <nav className={styles.tabs} role="tablist" aria-label="Your circles">
            {slugs.map((slug) => {
              const data = homes[slug]?.data;
              return (
                <button
                  key={slug}
                  role="tab"
                  aria-selected={slug === current}
                  className={`${styles.tab} ${slug === current ? styles.tabActive : ''}`}
                  onClick={() => select(slug)}
                >
                  {data?.circle.name ?? tokens[slug]?.circleName ?? slug}
                  {slug !== current && (data?.unreadCount ?? 0) > 0 && <span className={styles.tabDot} aria-hidden />}
                </button>
              );
            })}
          </nav>
        )}

        <Notes notes={notes} onDismiss={(n) => setNotes((all) => all.filter((x) => x !== n))} />

        {resolvingPreview || !current || (!currentHome?.data && !currentHome?.error) ? (
          <HomeSkeleton />
        ) : currentHome.error && !currentHome.data ? (
          <div className={styles.errorBox}>
            <h1 className={ui.title}>Couldn't load {tokens[current]?.circleName ?? 'this circle'}</h1>
            <p className={ui.sub}>Check your connection and try again.</p>
            <div className={ui.actions}>
              <button className={ui.secondary} onClick={() => window.location.reload()}>
                Try again
              </button>
            </div>
          </div>
        ) : currentHome.data ? (
          <CircleView
            key={current}
            home={currentHome.data}
            pushKey={publicKey.data ?? null}
            deviceToken={tokens[current]!.token}
            buildHref={builder?.ownsCurrent ? builder.href : null}
          />
        ) : null}
      </main>
      {settings}
    </>
  );
}

/**
 * The owner's way into the builder from their own launcher: shown only while this browser holds a
 * builder key, and straight to the current circle's page when that circle is one of theirs.
 */
function useBuilderLink(currentCircleId: string | null): { href: string; ownsCurrent: boolean } | null {
  const accountKey = useAccountKey();
  const owned = useCachedQuery(api.circles.list, accountKey ? { accountKey } : 'skip').data;
  if (!accountKey) return null;
  const mine = owned?.find((c) => c._id === currentCircleId);
  return mine ? { href: `/builder/${mine.slug}`, ownsCurrent: true } : { href: '/builder', ownsCurrent: false };
}

/** How many tiles get their document prefetched: the first screenful on a phone. */
const PREFETCH_TILES = 6;

function CircleView({
  home,
  pushKey,
  deviceToken,
  buildHref,
}: {
  home: HomeData;
  pushKey: string | null;
  deviceToken: string;
  /** The builder's page for this circle, when this browser's builder key owns it. */
  buildHref: string | null;
}) {
  const buildLink = home.preview ? null : buildHref;
  const others = home.members.filter((m) => m.id !== home.me.id);
  const prefetch = home.apps.slice(0, PREFETCH_TILES).map((a) => a.path);
  useEffect(() => {
    for (const path of prefetch) prefetchDocument(path);
  }, [prefetch.join('\n')]);

  return (
    <>
      {!home.preview && <NotifyCard pushKey={pushKey} deviceToken={deviceToken} />}
      <section className={styles.hello}>
        <h1 className={ui.title}>Hi, {home.me.name}</h1>
        <div className={styles.people}>
          {others.length > 0 && (
            <span className={styles.faces} aria-hidden>
              {others.slice(0, 4).map((m) => (
                <MemberAvatar
                  key={m.id}
                  name={m.name}
                  avatarUrl={m.avatarUrl}
                  className={styles.face}
                  style={{ background: avatarTint(m.avatarHue) }}
                />
              ))}
            </span>
          )}
          <span className={styles.peopleText}>
            {home.circle.name}
            {others.length > 0 &&
              ` with ${others.length <= 3 ? listNames(others.map((m) => m.name)) : `${others.length} others`}`}
          </span>
        </div>
      </section>

      {!home.preview && <Prompts pushKey={pushKey} deviceToken={deviceToken} />}

      {home.apps.length === 0 ? (
        <div className={styles.emptyApps}>
          <span className={styles.emptyIcon} aria-hidden>
            <AddSquareIcon size={26} />
          </span>
          {buildLink ? (
            <>
              <p>
                {home.circle.name} is ready for its first app. Describe what you need, like a chore chart or a shopping
                list, and it shows up here and on everyone's phone.
              </p>
              <Link to={buildLink} className={`${ui.primary} ${styles.emptyAction}`} data-testid="home-build-cta">
                <WrenchIcon size={18} />
                Build your first app
              </Link>
            </>
          ) : (
            <p>Nothing here yet. When someone sends an app to {home.circle.name}, it appears here.</p>
          )}
        </div>
      ) : (
        <ul className={styles.grid}>
          {home.apps.map((app, i) => (
            <li key={app.id} style={{ '--i': i } as CSSProperties}>
              <a href={app.path} className={styles.tile}>
                <span className={styles.tileIcon} style={{ background: tint(app.slug) }}>
                  {app.icon && isImageIcon(app.icon) ? (
                    <img src={`${app.path}${app.icon.replace(/^\.?\//, '')}`} alt="" />
                  ) : (
                    <span className={styles.emoji}>{app.icon ?? initial(app.name)}</span>
                  )}
                </span>
                <span className={styles.tileName}>{app.name}</span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/**
 * From the Home Screen: a card at the top of Home until notifications are on (or explained
 * as off in Settings). "Not now" hides it for a week; Settings keeps the toggle.
 */
function NotifyCard({ pushKey, deviceToken }: { pushKey: string | null; deviceToken: string }) {
  const [permission, setPermission] = useState(pushPermission);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const state = notifyCardState({
    standalone: isStandalone(),
    permission,
    pushConfigured: pushKey !== null,
    snoozedUntil: snoozedUntil(NOTIFY_CARD),
    now: Date.now(),
  });
  if (hidden || !state || !pushKey) return null;
  const later = () => {
    snooze(NOTIFY_CARD, NOTIFY_CARD_SNOOZE_MS);
    setHidden(true);
  };

  return (
    <div className={`${styles.prompt} ${styles.notifyCard}`} data-testid="notify-card" data-state={state} role="region" aria-label="Notifications">
      <div className={styles.promptHead}>
        <span className={styles.promptIcon} aria-hidden>
          <BellIcon size={22} />
        </span>
        {state === 'ask' ? (
          <div className={styles.promptText}>
            <strong>Turn on notifications</strong>
            <span>So you hear when someone sends an app or a reminder.</span>
          </div>
        ) : (
          <div className={styles.promptText}>
            <strong>Notifications are off</strong>
            <span>
              {isIos()
                ? 'To hear when someone sends an app or a reminder, open iOS Settings → Notifications → Bolt Circles and allow notifications.'
                : 'To hear when someone sends an app or a reminder, allow notifications for this site in your browser settings.'}
            </span>
          </div>
        )}
      </div>
      <div className={styles.promptActions}>
        <button className={ui.textButton} onClick={later}>
          {state === 'ask' ? 'Not now' : 'Got it'}
        </button>
        {state === 'ask' && (
          <button
            className={`${ui.primary} ${styles.small}`}
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void requestPush(pushKey, [deviceToken])
                .then((p) => setPermission(p))
                .finally(() => setBusy(false));
            }}
          >
            Turn on
          </button>
        )}
      </div>
    </div>
  );
}

/** At most one nudge outside the Home Screen app: install (iOS Safari / native prompt) or turn on notifications. */
function Prompts({ pushKey, deviceToken }: { pushKey: string | null; deviceToken: string }) {
  const canPrompt = useCanPromptInstall();
  const [permission, setPermission] = useState(pushPermission);
  const [hidden, setHidden] = useState<Record<string, boolean>>({});
  const [showSteps, setShowSteps] = useState(false);
  const standalone = isStandalone();
  const hide = (what: string) => {
    dismiss(what);
    setHidden((h) => ({ ...h, [what]: true }));
  };

  if (!standalone && (isIos() || canPrompt) && !isDismissed('install') && !hidden.install) {
    return (
      <div className={styles.prompt}>
        <div className={styles.promptHead}>
          <span className={styles.promptIcon} aria-hidden>
            <AddSquareIcon size={22} />
          </span>
          <div className={styles.promptText}>
            <strong>Add Bolt Circles to your Home Screen</strong>
            <span>New apps and notifications reach you there.</span>
          </div>
        </div>
        {showSteps && <IosInstallSteps />}
        <div className={styles.promptActions}>
          <button className={ui.textButton} onClick={() => hide('install')}>
            Not now
          </button>
          {isIos() ? (
            !showSteps && (
              <button className={`${ui.primary} ${styles.small}`} onClick={() => setShowSteps(true)}>
                Show me how
              </button>
            )
          ) : (
            <button className={`${ui.primary} ${styles.small}`} onClick={() => void promptInstall()}>
              Install
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!standalone && pushKey && permission === 'default' && !isDismissed('push') && !hidden.push) {
    return (
      <div className={styles.prompt}>
        <div className={styles.promptHead}>
          <span className={styles.promptIcon} aria-hidden>
            <BellIcon size={22} />
          </span>
          <div className={styles.promptText}>
            <strong>Turn on notifications</strong>
            <span>Know when someone sends you something, or an app reminds you.</span>
          </div>
        </div>
        <div className={styles.promptActions}>
          <button className={ui.textButton} onClick={() => hide('push')}>
            Not now
          </button>
          <button
            className={`${ui.primary} ${styles.small}`}
            onClick={() => {
              void requestPush(pushKey, [deviceToken]).then((p) => setPermission(p));
            }}
          >
            Turn on
          </button>
        </div>
      </div>
    );
  }
  return null;
}

function UpdatePill() {
  const [updating, setUpdating] = useState(false);
  return (
    <div className={styles.updatePill} role="status" data-testid="update-pill">
      <button
        className={styles.updateAction}
        disabled={updating}
        onClick={() => {
          setUpdating(true);
          void applyUpdate();
        }}
      >
        A new version is ready — <strong>{updating ? 'Updating…' : 'Update'}</strong>
      </button>
      <button className={styles.updateClose} onClick={dismissUpdate} aria-label="Dismiss">
        ×
      </button>
    </div>
  );
}

function Notes({ notes, onDismiss }: { notes: string[]; onDismiss: (note: string) => void }) {
  if (notes.length === 0) return null;
  return (
    <div className={styles.notes}>
      {notes.map((n) => (
        <div key={n} className={ui.note} role="status">
          <p>{n}</p>
          <button className={styles.noteClose} onClick={() => onDismiss(n)} aria-label="Dismiss">
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

/** Shown while cached tiles wait for the live home, only once the wait gets noticeable. */
function Updating() {
  const show = useDelayed(600);
  if (!show) return null;
  return (
    <span className={styles.updating} role="status" data-testid="updating">
      Updating…
    </span>
  );
}

/** First visit on this device (nothing cached): the home's layout in neutral blocks. */
function HomeSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading your apps" data-testid="home-skeleton">
      <div className={styles.hello}>
        <div className={ui.skeleton} style={{ height: 36, width: 190, borderRadius: 10 }} />
        <div className={ui.skeleton} style={{ height: 18, width: 230, marginTop: 14, borderRadius: 8 }} />
      </div>
      <div className={styles.grid} aria-hidden>
        {[0, 1, 2].map((i) => (
          <div key={i} className={`${styles.tile} ${styles.tileSkeleton}`}>
            <span className={`${ui.skeleton} ${styles.tileIcon}`} />
            <span className={`${ui.skeleton} ${styles.tileNameSkeleton}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

function listNames(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
